/* Green Gorilla Fruits — coupon auto-apply
 * --------------------------------------------------------------
 * When the visitor arrives on /cart or /checkout and a stored
 * referral coupon from referral-redeem.js is present, redirects
 * once to /discount/<code> so Shopify applies the discount and
 * sends them back to the cart with the coupon attached.
 *
 * Public API: window.GG_CouponAutoApply.run()
 */
(function () {
  'use strict';

  const COUPON_STORAGE_KEY = 'gg_referral_coupon';
  // localStorage (not sessionStorage) so a redirect loop cannot recur after
  // a navigation that lost the session-scoped flag (e.g. private browsing).
  const APPLIED_FLAG_KEY = 'gg_referral_coupon_last_applied';
  const ATTEMPT_COUNTER_KEY = 'gg_referral_coupon_apply_attempts';
  const PENDING_CHECKOUT_KEY = 'gg_referral_pending_checkout';
  const MAX_APPLY_ATTEMPTS = 2;
  const CART_PATHS = ['/cart', '/checkout'];

  function readCoupon() {
    try {
      const raw = localStorage.getItem(COUPON_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.code) return null;
      if (parsed.expires_at) {
        const expiry = Date.parse(parsed.expires_at);
        if (!isNaN(expiry) && Date.now() > expiry) {
          clearCoupon();
          return null;
        }
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function clearCoupon() {
    try {
      localStorage.removeItem(COUPON_STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function isCartPath() {
    const path = window.location.pathname;
    return CART_PATHS.some((prefix) => path === prefix || path.indexOf(prefix + '/') === 0);
  }

  function alreadyApplied(code) {
    try {
      return localStorage.getItem(APPLIED_FLAG_KEY) === code;
    } catch (e) {
      // If storage is unavailable we cannot guarantee loop prevention,
      // so refuse to redirect — better to skip auto-apply than to loop.
      return true;
    }
  }

  function markApplied(code) {
    try {
      localStorage.setItem(APPLIED_FLAG_KEY, code);
    } catch (e) {
      /* ignore — alreadyApplied() returns true on read failure so we stop here anyway */
    }
  }

  function clearAppliedFlag() {
    try {
      localStorage.removeItem(APPLIED_FLAG_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function readAttempts() {
    try {
      const raw = localStorage.getItem(ATTEMPT_COUNTER_KEY);
      const n = raw == null ? 0 : Number(raw);
      return Number.isFinite(n) && n > 0 ? n : 0;
    } catch (e) {
      return 0;
    }
  }

  function bumpAttempts() {
    try {
      localStorage.setItem(ATTEMPT_COUNTER_KEY, String(readAttempts() + 1));
    } catch (e) {
      /* ignore */
    }
  }

  function resetAttempts() {
    try {
      localStorage.removeItem(ATTEMPT_COUNTER_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  // Verify that the discount actually landed on the cart. Shopify silently
  // rejects unknown / inactive / password-blocked /discount/<code> redirects
  // — the 302 fires successfully but the cart never sees the code — so the
  // applied flag alone cannot be trusted. Returns:
  //   true  → discount confirmed present in cart
  //   false → cart fetched OK, discount NOT present (= previous redirect was rejected)
  //   null  → network/parse error, cannot determine (conservatively skip retry)
  async function discountAppliedOnCart(code) {
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const cart = await res.json();
      // Shopify normalizes discount codes to uppercase; compare case-insensitively
      // so the redeem endpoint's casing contract does not silently break detection.
      const target = String(code).toUpperCase();
      const eq = (s) => typeof s === 'string' && s.toUpperCase() === target;
      const codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
      if (codes.some((d) => d && eq(d.code) && d.applicable !== false)) return true;
      const apps = Array.isArray(cart.cart_level_discount_applications)
        ? cart.cart_level_discount_applications
        : [];
      if (apps.some((a) => a && (eq(a.title) || eq(a.code)))) return true;
      return false;
    } catch (e) {
      return null;
    }
  }

  function consumePendingCheckout() {
    try {
      if (sessionStorage.getItem(PENDING_CHECKOUT_KEY) === '1') {
        sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
        return true;
      }
    } catch (e) {
      /* ignore */
    }
    return false;
  }

  function buildSafeReturn() {
    const path = window.location.pathname || '/cart';
    const search = window.location.search || '';
    const combined = path + search;
    // Require a same-origin absolute path; reject protocol-relative URLs.
    if (!combined.startsWith('/') || combined.startsWith('//')) return '/cart';
    return combined;
  }

  async function run() {
    if (!isCartPath()) return;
    const coupon = readCoupon();
    if (!coupon) return;

    if (alreadyApplied(coupon.code)) {
      const verdict = await discountAppliedOnCart(coupon.code);
      if (!isCartPath()) return;
      if (verdict === true) {
        resetAttempts();
        return;
      }
      if (verdict === null) return;
      // verdict === false: previous redirect was silently rejected.
      // Bound retries so a permanently-rejected code (expired, region-locked,
      // password protection still on) cannot trap the user in an endless
      // /cart ↔ /discount loop. After MAX_APPLY_ATTEMPTS we drop the coupon.
      if (readAttempts() >= MAX_APPLY_ATTEMPTS) {
        clearCoupon();
        clearAppliedFlag();
        resetAttempts();
        return;
      }
      clearAppliedFlag();
    }

    bumpAttempts();
    markApplied(coupon.code);
    // When the referral-checkout-guard initiated the chain, route directly to /checkout instead of looping back to /cart.
    const returnTo = consumePendingCheckout() ? '/checkout' : buildSafeReturn();
    const url =
      '/discount/' + encodeURIComponent(coupon.code) + '?redirect=' + encodeURIComponent(returnTo);
    window.location.replace(url);
  }

  window.GG_CouponAutoApply = { run, clear: clearCoupon };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      run().catch(() => {});
    });
  } else {
    run().catch(() => {});
  }
})();
